import { Card } from '@/components/ui/card';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Text } from '@/components/ui/typography';
import { expandWildcards, type GrantedPermission } from '@/models/permission';

import { type Role } from '../models/role';

// Human label per permission resource segment. An unmapped segment falls back to
// the raw string: the backend can add resources without breaking this view, and
// no authorization behavior is derived from these labels.
const RESOURCE_LABELS: Record<string, string> = {
  admin: 'Administration',
  bans: 'Bans',
  beacons: 'Beacons',
  bundles: 'Bundles',
  gifts: 'Gifts',
  inventory: 'Inventory',
  inventory_transactions: 'Inventory Transactions',
  items: 'Items',
  languages: 'Languages',
  players: 'Players',
  sandboxes: 'Sandboxes',
  seasonpass: 'Season Passes',
  store: 'Store',
  store_transactions: 'Store Transactions',
  translations: 'Translations',
};

// Wildcards are expanded first: a Role granting 'store:*' lists the five store
// actions it really confers, rather than a '*' the reader has to decode.
export const groupByResource = (permissions: GrantedPermission[]) => {
  const groups = new Map<string, string[]>();
  for (const { resource, action } of expandWildcards(permissions)) {
    const actions = groups.get(resource) ?? [];
    actions.push(action);
    groups.set(resource, actions);
  }

  return [...groups]
    .map(([resource, actions]) => ({
      resource,
      label: RESOURCE_LABELS[resource] ?? resource,
      actions: [...actions].sort(),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

interface RoleSheetProps {
  role: Role | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const RoleSheet = ({ role, open, onOpenChange }: RoleSheetProps) => {
  const groups = groupByResource(role?.permissions ?? []);
  const permissionCount = groups.reduce(
    (total, { actions }) => total + actions.length,
    0,
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex flex-col">
        <SheetHeader className="p-0">
          <SheetTitle className="font-mono">{role?.name}</SheetTitle>
          <SheetDescription>{role?.description}</SheetDescription>
        </SheetHeader>
        <Text variant="muted">
          {permissionCount}{' '}
          {permissionCount === 1 ? 'permission' : 'permissions'} across{' '}
          {groups.length} {groups.length === 1 ? 'resource' : 'resources'}
        </Text>
        <div className="flex-1 space-y-3 overflow-y-auto">
          {groups.length === 0 && (
            <Text variant="muted">This Role grants no permission.</Text>
          )}
          {groups.map(({ resource, label, actions }) => (
            <Card key={resource} className="gap-1 p-3">
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-medium">{label}</span>
                <span className="text-xs text-muted-foreground">
                  {actions.length} {actions.length === 1 ? 'action' : 'actions'}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                {actions.join(', ')}
              </p>
            </Card>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
};
